/*
 * App — top-level state machine: idle → loading → results | empty | error.
 *
 * - Loads /corpus on mount for the headline frame count (also fed into the
 *   IdleScreen grid).
 * - On submit: POST /queries, then open SSE; events accumulate into a single
 *   `traceEvents` array driving the Trace panel and the result-card list.
 * - stream_end flips the phase. We then GET /queries/{id}/results for the
 *   canonical card payload (the verify events carry previews but /results is
 *   the source of truth — picks up clip metadata enriched by the backend).
 * - EventSource is cancelled on unmount and on phase reset so we never leak.
 */

import React from 'react';
import {
  createQuery,
  getCorpus,
  getResults,
  saveComment,
  streamEvents,
} from './api.js';
import { Header } from './components/Header.jsx';
import { IdleScreen } from './components/IdleScreen.jsx';
import { PastQueriesDrawer } from './components/PastQueries.jsx';
import { TracePanel } from './components/Trace.jsx';
import {
  LoadingSkeleton,
  ResultCard,
  ResultsHeader,
} from './components/Results.jsx';
import { EmptyScreen } from './components/EmptyScreen.jsx';
import { ErrorScreen } from './components/ErrorScreen.jsx';

const INITIAL_PHASE = 'idle';

function useCorpusFrameCount() {
  const [frames, setFrames] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    getCorpus()
      .then((data) => {
        if (!cancelled) setFrames(data.total_frames);
      })
      .catch(() => {
        if (!cancelled) setFrames(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return frames;
}

function useElapsedClock(active) {
  const [ms, setMs] = React.useState(0);
  const startedAt = React.useRef(0);

  React.useEffect(() => {
    if (!active) {
      setMs(0);
      return undefined;
    }
    startedAt.current = performance.now();
    let frame;
    const tick = () => {
      setMs(performance.now() - startedAt.current);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active]);

  return ms;
}

function ResultsLayout({
  phase,
  query,
  traceEvents,
  elapsedMs,
  traceCollapsed,
  onToggleTrace,
  results,
  comments,
  commentStatus,
  onSaveComment,
  onNewSearch,
}) {
  const trackTemplate = traceCollapsed ? '44px' : '440px';
  return (
    <div className="rlayout" style={{ gridTemplateColumns: `minmax(0, 1fr) ${trackTemplate}` }}>
      <main className="rlayout-main">
        {phase === 'loading' && results.length === 0 && <LoadingSkeleton />}
        {phase === 'loading' && results.length > 0 && (
          <div className="results-state">
            <ResultsHeader query={query} count={results.length} />
            <div className="results-list">
              {results.map((r, i) => (
                <ResultCard
                  key={r.id}
                  result={r}
                  index={i}
                  comment={comments[r.id]?.text}
                  commentStatus={commentStatus[r.id]}
                  onComment={(text) => onSaveComment(r.id, text)}
                />
              ))}
            </div>
          </div>
        )}
        {phase === 'results' && (
          <div className="results-state">
            <ResultsHeader query={query} count={results.length} />
            <div className="results-list">
              {results.map((r, i) => (
                <ResultCard
                  key={r.id}
                  result={r}
                  index={i}
                  comment={comments[r.id]?.text}
                  commentStatus={commentStatus[r.id]}
                  onComment={(text) => onSaveComment(r.id, text)}
                />
              ))}
            </div>
            <footer className="results-foot">
              <button type="button" className="empty-btn-ghost mono" onClick={onNewSearch}>
                start over
              </button>
              <div className="mono micro" style={{ color: 'var(--ink-3)' }}>
                {Object.keys(comments).length > 0 && (
                  <span>
                    {Object.keys(comments).length} curator{' '}
                    {Object.keys(comments).length === 1 ? 'note' : 'notes'} saved ·{' '}
                  </span>
                )}
                end of results
              </div>
            </footer>
          </div>
        )}
      </main>

      <div className={`rlayout-aside ${traceCollapsed ? 'rlayout-aside-collapsed' : ''}`}>
        <TracePanel
          events={traceEvents}
          status={phase}
          query={query}
          elapsedMs={elapsedMs}
          collapsed={traceCollapsed}
          onToggle={onToggleTrace}
        />
      </div>

      <style>{`
        .rlayout {
          display: grid;
          min-height: calc(100vh - 78px);
          transition: grid-template-columns .3s cubic-bezier(.4,0,.2,1);
        }
        .rlayout-main { padding: 12px 48px 60px; min-width: 0; }
        .rlayout-aside { border-left: 1px solid var(--rule); position: sticky; top: 78px; height: calc(100vh - 78px); }
        .results-list { display: flex; flex-direction: column; }
        .results-foot { display: flex; justify-content: space-between; align-items: center; padding-top: 22px; }
        .empty-btn-ghost { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; padding: 9px 18px; border: 1px solid var(--rule-2); background: transparent; color: var(--ink); }
        .empty-btn-ghost:hover { border-color: var(--ink); }
      `}</style>
    </div>
  );
}

export default function App() {
  const [phase, setPhase] = React.useState(INITIAL_PHASE);
  const [query, setQuery] = React.useState('');
  const [queryId, setQueryId] = React.useState(null);
  const [traceEvents, setTraceEvents] = React.useState([]);
  const [results, setResults] = React.useState([]);
  const [comments, setComments] = React.useState({});
  const [commentStatus, setCommentStatus] = React.useState({});
  const [traceCollapsed, setTraceCollapsed] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const corpusFrameCount = useCorpusFrameCount();
  const elapsedMs = useElapsedClock(phase === 'loading');

  // Reference to the active EventSource so we can cancel on unmount / reset.
  const streamRef = React.useRef(null);

  const cancelStream = React.useCallback(() => {
    if (streamRef.current) {
      streamRef.current.cancel();
      streamRef.current = null;
    }
  }, []);

  React.useEffect(() => cancelStream, [cancelStream]);

  const resetTransient = React.useCallback(() => {
    cancelStream();
    setTraceEvents([]);
    setResults([]);
    setComments({});
    setCommentStatus({});
  }, [cancelStream]);

  const goIdle = React.useCallback(() => {
    resetTransient();
    setQuery('');
    setQueryId(null);
    setPhase('idle');
  }, [resetTransient]);

  const runQuery = React.useCallback(
    async (text) => {
      resetTransient();
      setQuery(text);
      setPhase('loading');
      try {
        const { query_id: id } = await createQuery(text);
        setQueryId(id);
        streamRef.current = streamEvents(id, {
          onEvent: (event) => {
            setTraceEvents((prev) => [...prev, event]);
          },
          onEnd: async (status) => {
            streamRef.current = null;
            await finishQuery(id, status, setResults, setPhase);
          },
          onError: () => {
            // onEnd will still fire with the latest status; nothing to do here.
          },
        });
      } catch (err) {
        const networkError = {
          kind: 'error',
          t_ms: 0,
          message: err?.message || 'network error',
        };
        setTraceEvents((prev) => [...prev, networkError]);
        setPhase('error');
      }
    },
    [resetTransient],
  );

  const retry = React.useCallback(() => {
    if (query) runQuery(query);
  }, [query, runQuery]);

  const handleSaveComment = React.useCallback(
    async (resultId, text) => {
      if (!queryId) return;
      setCommentStatus((prev) => ({ ...prev, [resultId]: 'saving' }));
      // Optimistic update so the saved note appears immediately; we revert if
      // the POST fails so the curator sees the failure.
      const previous = comments[resultId];
      setComments((prev) => ({ ...prev, [resultId]: { text, saved_at: null } }));
      try {
        const response = await saveComment(queryId, resultId, text);
        setComments((prev) => ({
          ...prev,
          [resultId]: { text, saved_at: response.saved_at },
        }));
        setCommentStatus((prev) => ({ ...prev, [resultId]: 'saved' }));
      } catch (err) {
        setComments((prev) => {
          const next = { ...prev };
          if (previous) next[resultId] = previous;
          else delete next[resultId];
          return next;
        });
        setCommentStatus((prev) => ({ ...prev, [resultId]: 'error' }));
      }
    },
    [comments, queryId],
  );

  return (
    <div className="app">
      <Header
        phase={phase}
        query={query}
        elapsed={elapsedMs / 1000}
        onNewSearch={goIdle}
        onOpenHistory={() => setHistoryOpen(true)}
      />
      <PastQueriesDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />

      {phase === 'idle' && (
        <IdleScreen
          query={query}
          setQuery={setQuery}
          onRun={runQuery}
          corpusFrameCount={corpusFrameCount}
        />
      )}

      {(phase === 'loading' || phase === 'results') && (
        <ResultsLayout
          phase={phase}
          query={query}
          traceEvents={traceEvents}
          elapsedMs={elapsedMs}
          traceCollapsed={traceCollapsed}
          onToggleTrace={() => setTraceCollapsed((c) => !c)}
          results={results}
          comments={comments}
          commentStatus={commentStatus}
          onSaveComment={handleSaveComment}
          onNewSearch={goIdle}
        />
      )}

      {phase === 'empty' && (
        <EmptyScreen
          query={query}
          traceEvents={traceEvents}
          onNewSearch={goIdle}
          onRefine={goIdle}
        />
      )}

      {phase === 'error' && (
        <ErrorScreen
          query={query}
          traceEvents={traceEvents}
          onRetry={retry}
          onNewSearch={goIdle}
        />
      )}
    </div>
  );
}

async function finishQuery(queryId, status, setResults, setPhase) {
  if (status === 'error') {
    setPhase('error');
    return;
  }
  try {
    const data = await getResults(queryId);
    const fetched = data.results || [];
    setResults(fetched);
    if (status === 'empty' || fetched.length === 0) {
      setPhase(fetched.length === 0 ? 'empty' : 'results');
    } else {
      setPhase('results');
    }
  } catch (err) {
    setPhase('error');
  }
}
